import React from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button,
  Typography, Stack, Table, TableRow, TableCell, TableBody,
  Chip, useTheme, alpha, Box, Divider,
} from '@mui/material';
import { CheckCircle, ErrorOutline, SkipNext, PictureAsPdfOutlined } from '@mui/icons-material';
import { HandoverPreviewRow, HandoverRowResult } from '../../../services/admissions/handoverService';
import { usePdfExport, useSchoolBranding } from '../../../utils/pdf';
import { HandoverSummaryDocument } from '../pdf';
import useTimezone from '../../../hooks/useTimezone';
import BusyIcon from '../../common/BusyIcon';

interface Props {
  open: boolean;
  results: HandoverRowResult[];
  onClose: () => void;
  /** Optional context for the generated PDF title. */
  cycleName?: string;
  /** Rows that were run — gives the summary PDF names and classes. */
  previewRows?: HandoverPreviewRow[];
}

/**
 * HandoverResultsDialog — post-run summary + per-row results. Shows what
 * succeeded (with GR and roll number), what was skipped (with
 * reason), what failed (with error). Non-dismissive on failures — operator
 * reads the reasons before closing.
 */

const HandoverResultsDialog: React.FC<Props> = ({ open, results, onClose, cycleName, previewRows }) => {
  const theme = useTheme();

  const nameOf = (id: string) => previewRows?.find(p => p.applicationId === id)?.name ?? 'Applicant';

  const enrolled = results.filter(r => r.outcome === 'enrolled');
  const skipped  = results.filter(r => r.outcome === 'skipped');
  const failed   = results.filter(r => r.outcome === 'failed');

  const { branding } = useSchoolBranding();
  const { getToday } = useTimezone();
  const { generate: generatePdf, loading: pdfLoading } = usePdfExport();

  const downloadSummary = async () => {
    const stamp = getToday();
    await generatePdf(
      <HandoverSummaryDocument
        results={results}
        previewRows={previewRows}
        cycleName={cycleName}
        branding={branding}
        meta={{
          title: 'Handover Summary',
          subtitle: cycleName,
          generatedAt: new Date().toISOString(),
        }}
      />,
      `handover-summary-${stamp}.pdf`,
    );
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle component="div">
        <Typography variant="h6" fontWeight={700}>Handover results</Typography>
        <Typography variant="caption" color="text.secondary">
          {results.length} row{results.length === 1 ? '' : 's'} processed.
        </Typography>
      </DialogTitle>
      <DialogContent>
        <Stack
          direction="row"
          spacing={2}
          sx={{
            p: 2, mb: 2, borderRadius: 2,
            border: `1px solid ${theme.palette.divider}`,
            bgcolor: alpha(theme.palette.text.primary, 0.015),
          }}
        >
          <ResultMetric label="Enrolled" value={enrolled.length} tone="success" />
          <ResultMetric label="Skipped"  value={skipped.length}  tone="info" />
          <ResultMetric label="Failed"   value={failed.length}   tone="error" />
        </Stack>

        {enrolled.length > 0 && (
          <ResultsSection
            title="Enrolled"
            tone="success"
            rows={enrolled.map(r => ({
              id: r.applicationId,
              primary: nameOf(r.applicationId),
              secondary: `GR ${r.grNumber ?? '—'} · Roll ${r.rollNumber ?? '—'}`,
            }))}
          />
        )}
        {skipped.length > 0 && (
          <ResultsSection
            title="Skipped"
            tone="info"
            rows={skipped.map(r => ({
              id: r.applicationId,
              primary: nameOf(r.applicationId),
              secondary: r.reason || 'Skipped',
            }))}
          />
        )}
        {failed.length > 0 && (
          <ResultsSection
            title="Failed"
            tone="error"
            rows={failed.map(r => ({
              id: r.applicationId,
              primary: nameOf(r.applicationId),
              secondary: r.reason || 'Server error',
            }))}
          />
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button
          variant="outlined"
          startIcon={<BusyIcon busy={pdfLoading}><PictureAsPdfOutlined /></BusyIcon>}
          onClick={downloadSummary}
          disabled={pdfLoading || results.length === 0}
        >
          {pdfLoading ? 'Preparing…' : 'Download summary'}
        </Button>
        <Button variant="contained" disableElevation onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

const ResultMetric: React.FC<{ label: string; value: number; tone: 'success' | 'info' | 'error' }> = ({ label, value, tone }) => {
  const theme = useTheme();
  const color = tone === 'success' ? theme.palette.success.main
    : tone === 'error' ? theme.palette.error.main
    : theme.palette.info.main;
  return (
    <Box sx={{ flex: 1 }}>
      <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 600 }}>
        {label}
      </Typography>
      <Typography variant="h5" fontWeight={700} fontFamily="monospace" sx={{ color, mt: 0.5 }}>
        {value}
      </Typography>
    </Box>
  );
};

const ResultsSection: React.FC<{
  title: string;
  tone: 'success' | 'info' | 'error';
  rows: Array<{ id: string; primary: string; secondary: string }>;
}> = ({ title, tone, rows }) => {
  const theme = useTheme();
  const Icon = tone === 'success' ? CheckCircle : tone === 'info' ? SkipNext : ErrorOutline;
  const color = tone === 'success' ? theme.palette.success.main
    : tone === 'error' ? theme.palette.error.main
    : theme.palette.info.main;
  return (
    <Box sx={{ mb: 2 }}>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
        <Icon sx={{ fontSize: 18, color }} />
        <Typography variant="subtitle2" fontWeight={700} sx={{ color }}>
          {title}
        </Typography>
        <Chip size="small" label={rows.length} sx={{ height: 18, fontSize: 10, color, bgcolor: alpha(color, 0.1), border: 'none' }} />
      </Stack>
      <Table size="small">
        <TableBody>
          {rows.map(r => (
            <TableRow key={r.id}>
              <TableCell>
                <Typography variant="body2" fontWeight={600}>{r.primary}</Typography>
                <Typography variant="caption" color="text.secondary">{r.secondary}</Typography>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Box>
  );
};

void Divider;

export default HandoverResultsDialog;
